import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { ModuleService } from '@services/core/module.service';
import { EvaluationService } from '@services/core/evaluation.service';
import { EntityService } from '@services/core/entity.service';
import { NgClass } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormBuilder, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { typeIds, cloneLevels, groupIds, moduleValues, modulePayload } from './module-form.helper';
import { notifyModuleError } from './module-notify.helper';

@Component({
  selector: 'app-modules',
  templateUrl: './modules.component.html',
  styleUrls: ["./modules.component.scss"],
  imports: [PanelComponent, NgClass, FormsModule, ReactiveFormsModule, NgbTooltipModule, NgSelectComponent],
  providers: [ModuleService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class ModulesComponent implements OnInit {
  protected modules = signal<any[]>([])

  private moduleService = inject(ModuleService)
  private evaluationService = inject(EvaluationService)
  private entityService = inject(EntityService)
  private fb = inject(FormBuilder)
  private destroyRef = inject(DestroyRef)

  protected allQuestionTypes = signal<any[]>([])
  protected selectedTypes: string[] = []
  protected editingLevels: any[] = []
  protected allGroups: any[] = []
  protected selectedGroupIds: string[] = []

  protected moduleForm = this.fb.group({
    id: [null],
    name: ['', Validators.required],
    acronym: ['', Validators.required],
    routeName: [''],
    description: [''],
    enabled: [true],
    evaluateAllMonths: [false],
    scoringMode: ['monthly'],
  })

  protected editing = false
  protected showForm = false

  ngOnInit() {
    this.loadModules();
    this.loadQuestionTypes();
    this.loadGroups();
  }

  loadModules() {
    this.moduleService.getModules().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.modules.set(res);
    });
  }

  loadQuestionTypes() {
    this.evaluationService.getQuestionTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.allQuestionTypes.set(res.filter((t: any) => t.enabled));
    });
  }

  loadGroups() {
    this.entityService.getGroupsEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groups: any[]) => this.allGroups = groups || []);
  }

  newModule() {
    this.moduleForm.reset({ enabled: true, evaluateAllMonths: false, scoringMode: 'monthly' });
    this.selectedTypes = [];
    this.editingLevels = [];
    this.selectedGroupIds = [];
    this.editing = false;
    this.showForm = true;
  }

  protected groupName(id: string): string {
    return this.allGroups.find((g: any) => g.id === id)?.name || id;
  }

  editModule(module) {
    this.selectedTypes = typeIds(module);
    this.editingLevels = cloneLevels(module);
    this.selectedGroupIds = groupIds(module);
    this.applyModule(module);
  }

  private applyModule(module) {
    this.moduleForm.patchValue(moduleValues(module));
    this.editing = true;
    this.showForm = true;
  }

  cancelForm() {
    this.showForm = false;
    this.moduleForm.reset();
    this.selectedTypes = [];
    this.editingLevels = [];
    this.selectedGroupIds = [];
  }

  toggleQuestionType(id: string) {
    const idx = this.selectedTypes.indexOf(id);
    if (idx > -1) this.selectedTypes.splice(idx, 1);
    else this.selectedTypes.push(id);
  }

  addLevel() {
    this.editingLevels.push({ minPercent: null, maxPercent: null, label: '', color: '#28a745' });
  }

  removeLevel(i: number) {
    this.editingLevels.splice(i, 1);
  }

  saveModule() {
    if (this.moduleForm.invalid) return;
    this.persistModule(this.buildPayload());
  }

  private buildPayload() {
    const raw = this.moduleForm.value;
    return modulePayload(raw, this.selectedTypes, this.editingLevels, this.selectedGroupIds);
  }

  private persistModule(payload: any) {
    const fresh = !payload.id;
    this.moduleService.upsertModule(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.afterSave(fresh),
      error: (err) => this.failSave(err),
    });
  }

  private afterSave(fresh: boolean) {
    this.loadModules();
    this.cancelForm();
    this.notifySaved(fresh);
  }

  private failSave(err: any) {
    notifyModuleError(err);
  }

  private notifySaved(isNew: boolean) {
    if (!isNew) {
      Swal.fire({ icon: 'success', title: 'Guardado', timer: 1500, showConfirmButton: false });
      return;
    }
    Swal.fire({
      icon: 'success',
      title: 'Módulo creado',
      html: 'Se creó la estructura de menú. Asígnala al rol deseado en <strong>Administración → Menús por Rol</strong>.',
    });
  }

  toggleModule(module) {
    const enabled = !module.enabled;
    this.moduleService.toggleModule(module.id, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadModules(),
      error: (err) => Swal.fire({ icon: 'error', title: 'Error', text: err.error?.error || 'Error al cambiar estado' }),
    });
  }
}
